<template>
  <div class="rband" :class="{ 'rband--tall': tall }" role="img" :aria-label="label">
    <div class="rband__track">
      <span
        v-for="(r, i) in ranges"
        :key="'u' + i"
        class="rband__used"
        :style="{ left: pct(r[0] - from), width: pct(r[1] - r[0] + 1) }"
      ></span>
      <span
        v-for="(g, i) in gaps"
        :key="'g' + i"
        class="rband__gap"
        :class="g.explained ? 'rband__gap--ok' : g.minor ? 'rband__gap--minor' : 'rband__gap--bad'"
        :style="{ left: pct(g.from - from), width: pct(g.count) }"
      ></span>
      <span v-if="marker !== null" class="rband__pos" :style="{ left: pct(marker + 1 - from) }"></span>
    </div>
    <div v-if="scale" class="rband__scale" aria-hidden="true">
      <span>{{ from }}</span>
      <span>{{ to }}</span>
    </div>
  </div>
</template>

<script>
// Abschnittsband einer Rolle (Rollen, Kundenwunsch 27.10.2026): die Rolle von Abschnitt from bis to als Spur, belegte
// Abschnitte dunkel, Lücken als Marke (rot unerklärt, gelb erklärt, grau einzelner Abschnitt = unkritisch, mindestens
// 3 px breit, damit einzelne Abschnitte sichtbar bleiben), der Strich zeigt die aktuelle Stelle der Rolle im Drucker.
// Props: ranges [[von, bis]], gaps [{ from, to, count, explained, minor }], from/to (Ausschnitt, Standard 0 bis
// Kapazität), marker (letzter Abschnitt oder null), scale (Beschriftung von/bis), tall (höher, im Detail).
export default {
  name: 'RollSectionBand',
  props: {
    ranges: { type: Array, default: () => [] },
    gaps: { type: Array, default: () => [] },
    from: { type: Number, default: 0 },
    to: { type: Number, required: true },
    marker: { type: Number, default: null },
    scale: { type: Boolean, default: false },
    tall: { type: Boolean, default: false }
  },
  computed: {
    span() {
      return Math.max(1, this.to - this.from + 1);
    },
    label() {
      const used = this.ranges.reduce((n, r) => n + r[1] - r[0] + 1, 0);
      const open = this.gaps.filter((g) => !g.explained && !g.minor).length;
      return 'Abschnitte ' + this.from + ' bis ' + this.to + ': ' + used + ' belegt'
        + (open ? ', ' + open + (open === 1 ? ' unerklärte Lücke' : ' unerklärte Lücken') : '');
    }
  },
  methods: {
    pct(n) {
      return Math.max(0, Math.min(100, (n / this.span) * 100)) + '%';
    }
  }
};
</script>

<style scoped>
.rband {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.rband__track {
  position: relative;
  height: 10px;
  border-radius: 2px;
  background: var(--d-trk);
  overflow: hidden;
}
.rband--tall .rband__track {
  height: 16px;
}
.rband__used {
  position: absolute;
  top: 0;
  bottom: 0;
  background: var(--d-std);
}
.rband__gap {
  position: absolute;
  top: 0;
  bottom: 0;
  min-width: 3px;
  transform: translateX(-1px);
}
.rband__gap--bad {
  background: var(--st-late);
}
.rband__gap--ok {
  background: var(--st-warn);
}
.rband__gap--minor {
  background: var(--ink-4);
}
.rband__pos {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 2px;
  background: var(--ink);
  transform: translateX(-1px);
}
.rband__scale {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  line-height: 14px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
</style>
